import { saveSideNavContractedToLocalStorage, saveSidebarItemsStateToLocalStorage } from '@/util/storage-utils'
import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export enum AuthDialogType {
  SIGN_IN = 'SIGN_IN',
  SIGN_UP = 'SIGN_UP',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
}

export type RetitleConversationAction = {
  visible: boolean
  conversationId: string | null
}

// Define types for Sidebar
export type SidebarItemState = {
  itemId: string
  isOpen: boolean
}

export enum SidebarItemId {
  Research = 'research',
  Drives = 'drives',
  Tools = 'tools',
  MedicalOverview = 'medical-overview',
}

// Default sidebar items state
const defaultSidebarItemsState: SidebarItemState[] = [
  { itemId: SidebarItemId.Tools, isOpen: false },
  { itemId: SidebarItemId.MedicalOverview, isOpen: false },
]

// Define a type for the slice state
interface UIState {
  showAuthDialog: boolean
  authDialogType: AuthDialogType
  authDialogTooltipMessage?: string
  sideNavCollapsed: boolean
  stripePortalDialogVisible: boolean
  showLegacyStripeCheckout: boolean
  legacyStripeCheckoutMessage: string
  referralDialogVisible: boolean
  // Opened when Firebase ends a session this tab did not sign out of (logout on another device).
  sessionEndedModalVisible: boolean
  sidebarItemsState: SidebarItemState[]
  isMobileSidebarOpen: boolean
  // Settings-revamp modal (gated behind SETTINGS_REVAMP_FOUNDATION). The section id and the
  // analytics sources are plain strings so this infrastructure slice does not depend on the
  // settings-modal feature or the analytics enums. The feature validates them on read.
  settingsModalOpen: boolean
  settingsModalSection: string | null
  settingsModalEntrySource: string | null
  settingsModalRosterEntrySource: string | null
}

export type OpenSettingsModalPayload = {
  section?: string
  settingsEntrySource?: string
  organizationRosterEntrySource?: string
}

// Define the initial state using that type
const initialState: UIState = {
  showAuthDialog: false,
  authDialogType: AuthDialogType.SIGN_UP,
  authDialogTooltipMessage: undefined,
  sideNavCollapsed: false,
  stripePortalDialogVisible: false,
  showLegacyStripeCheckout: false,
  legacyStripeCheckoutMessage: '',
  referralDialogVisible: false,
  sessionEndedModalVisible: false,
  sidebarItemsState: defaultSidebarItemsState,
  isMobileSidebarOpen: false,
  settingsModalOpen: false,
  settingsModalSection: null,
  settingsModalEntrySource: null,
  settingsModalRosterEntrySource: null,
}

export const uiStateSlice = createSlice({
  name: 'uiState',
  initialState,
  reducers: {
    openAuthDialog: (state, action: PayloadAction<{ authDialogType: AuthDialogType; tooltipMessage?: string }>) => {
      console.log('Opening auth dialog...')
      state.showAuthDialog = true
      state.authDialogType = action.payload.authDialogType
      state.authDialogTooltipMessage = action.payload.tooltipMessage
    },
    switchAuthDialog: (state, action: PayloadAction<AuthDialogType>) => {
      state.authDialogType = action.payload
    },
    closeAuthDialog: (state) => {
      state.showAuthDialog = false
    },
    // Only the user's own toggle is persisted, so automatic collapses (setSideNavCollapsed) cannot
    // overwrite their preference.
    toggleSideNavCollapsed: (state) => {
      state.sideNavCollapsed = !state.sideNavCollapsed
      saveSideNavContractedToLocalStorage(state.sideNavCollapsed)
    },
    setSideNavCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sideNavCollapsed = action.payload
    },
    setStripePortalDialogVisible: (state, action: PayloadAction<boolean>) => {
      state.stripePortalDialogVisible = action.payload
    },
    setLegacyStripeCheckoutVisible: (state, action: PayloadAction<boolean>) => {
      state.showLegacyStripeCheckout = action.payload
    },
    setLegacyStripeCheckoutMessage: (state, action: PayloadAction<string>) => {
      state.legacyStripeCheckoutMessage = action.payload
    },
    clearLegacyStripeCheckoutMessage: (state) => {
      state.legacyStripeCheckoutMessage = ''
    },
    setSessionEndedModalVisible: (state, action: PayloadAction<boolean>) => {
      state.sessionEndedModalVisible = action.payload
    },
    setReferralDialogVisible: (state, action: PayloadAction<boolean>) => {
      state.referralDialogVisible = action.payload
    },
    setSidebarItemOpen: (state, action: PayloadAction<string>) => {
      const itemId = action.payload
      let item = state.sidebarItemsState.find((item) => item.itemId === itemId)

      // If the item doesn't exist, add it to the state first (migration fallback)
      if (!item) {
        console.log(`[ui-state.slice] Adding missing sidebar item on-the-fly: ${itemId}`)
        const newItem: SidebarItemState = { itemId, isOpen: true } // Default to open since user is clicking to open
        state.sidebarItemsState.push(newItem)
        item = newItem
      } else {
        // Item exists, toggle it
        item.isOpen = !item.isOpen
      }

      saveSidebarItemsStateToLocalStorage(state.sidebarItemsState)
    },
    setSidebarItemsState: (state, action: PayloadAction<SidebarItemState[]>) => {
      state.sidebarItemsState = action.payload
    },
    setMobileSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isMobileSidebarOpen = action.payload
    },
    // Open the settings-revamp modal, optionally on a specific section. Omitting the section lets
    // the feature derive the default (first section for the user's role). A later open while the
    // modal is already visible keeps the entry source unless the new payload names one, and keeps
    // the roster source unless the new payload names one or targets a section other than members.
    openSettingsModal: (state, action: PayloadAction<OpenSettingsModalPayload | undefined>) => {
      const payload = action.payload
      const wasOpen = state.settingsModalOpen
      state.settingsModalOpen = true
      state.settingsModalSection = payload?.section ?? null
      if (!wasOpen || payload?.settingsEntrySource != null) {
        state.settingsModalEntrySource = payload?.settingsEntrySource ?? null
      }
      if (!wasOpen) {
        state.settingsModalRosterEntrySource = payload?.organizationRosterEntrySource ?? null
      } else if (payload?.organizationRosterEntrySource != null) {
        state.settingsModalRosterEntrySource = payload.organizationRosterEntrySource
      } else if (payload?.section != null && payload.section !== 'members') {
        state.settingsModalRosterEntrySource = null
      }
    },
    closeSettingsModal: (state) => {
      state.settingsModalOpen = false
      state.settingsModalSection = null
      state.settingsModalEntrySource = null
      state.settingsModalRosterEntrySource = null
    },
    // Switch the active section (desktop nav click, mobile drill-in). `null` returns to the mobile
    // section list.
    setSettingsModalSection: (state, action: PayloadAction<string | null>) => {
      state.settingsModalSection = action.payload
    },
  },
})

export const {
  openAuthDialog,
  switchAuthDialog,
  closeAuthDialog,
  toggleSideNavCollapsed,
  setSideNavCollapsed,
  setStripePortalDialogVisible,
  setLegacyStripeCheckoutVisible,
  setLegacyStripeCheckoutMessage,
  clearLegacyStripeCheckoutMessage,
  setReferralDialogVisible,
  setSessionEndedModalVisible,
  setSidebarItemOpen,
  setSidebarItemsState,
  setMobileSidebarOpen,
  openSettingsModal,
  closeSettingsModal,
  setSettingsModalSection,
} = uiStateSlice.actions

export default uiStateSlice.reducer
